import { Dialog, IconButton } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import intl from "react-intl-universal";
import { MdClose } from "react-icons/md";
import { TbFileSearch, TbRefresh } from "react-icons/tb";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { formatBaasConsoleConflictMessage } from "../../../../../Services/baasConsoleClient";
import { consultBaasEntityProposal } from "../../../../../Services/baasEntidadesApi";
import { BaasProposalSituationCard } from "../../components/BaasProposalSituationBadge";

const MODAL_CLASS =
  "!rounded-2xl !shadow-card app-card !m-4 !flex !flex-col !w-[calc(100%-2rem)] !max-w-lg !max-h-[85vh]";

const DetailRow = ({ label, value, mono = false }) => {
  if (value == null || value === "") return null;
  return (
    <div className="flex flex-col gap-0.5 border-b border-gray-100 py-2.5 last:border-b-0 dark:border-brand-dark-border sm:flex-row sm:items-baseline sm:justify-between sm:gap-4">
      <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <span
        className={`text-sm font-medium text-brand-dark dark:text-gray-100 sm:text-right ${
          mono ? "font-mono text-xs break-all" : ""
        }`}
      >
        {value}
      </span>
    </div>
  );
};

const formatPhone = (personalData) => {
  if (!personalData) return null;
  const ddd = personalData.localAreaCodeCellPhone;
  const number = personalData.cellPhoneNumber;
  if (ddd == null && number == null) return null;
  if (ddd != null && number != null) return `(${ddd}) ${number}`;
  return String(ddd ?? number);
};

/**
 * Manual GET `/v1/entities/{entityId}/proposal` (PaySmart proposal/last) from Contas detail.
 */
export const BaasAccountProposalModal = ({
  open,
  onClose,
  entityId = null,
  onProposalLoaded = null,
}) => {
  const { selectedInstance } = useBaasInstance();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [proposal, setProposal] = useState(null);

  const loadProposal = useCallback(async () => {
    if (!selectedInstance || !entityId) {
      setError(intl.get("baas.accounts.proposalModal.missingEntity"));
      setProposal(null);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const next = await consultBaasEntityProposal(selectedInstance, entityId);
      setProposal(next);
      if (next?.situation) {
        onProposalLoaded?.(next);
      }
      if (!next) {
        setError(intl.get("baas.accounts.proposalModal.empty"));
      }
    } catch (err) {
      setProposal(null);
      setError(
        formatBaasConsoleConflictMessage(
          err,
          err?.detail
            || err?.message
            || intl.get("baas.accounts.proposalModal.failed"),
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [entityId, onProposalLoaded, selectedInstance]);

  useEffect(() => {
    if (!open) {
      setLoading(false);
      setError("");
      setProposal(null);
      return undefined;
    }
    void loadProposal();
    return undefined;
  }, [open, loadProposal]);

  const personal = proposal?.personalData;
  const credit = proposal?.creditEngine;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      classes={{ paper: MODAL_CLASS }}
    >
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4 dark:border-brand-dark-border">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#e7a73f]">
            {intl.get("baas.accounts.proposalModal.eyebrow")}
          </p>
          <h2 className="mt-0.5 flex items-center gap-2 text-lg font-semibold text-brand-dark dark:text-gray-100">
            <TbFileSearch className="h-5 w-5 shrink-0 text-[#e7a73f]" />
            {intl.get("baas.accounts.proposalModal.title")}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.accounts.proposalModal.subtitle")}
          </p>
        </div>
        <IconButton
          aria-label={intl.get("baas.accounts.proposalModal.close")}
          onClick={onClose}
          size="small"
          className="!text-gray-500"
        >
          <MdClose />
        </IconButton>
      </div>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
        {loading ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.accounts.proposalModal.loading")}
          </p>
        ) : null}

        {error ? (
          <p
            role="alert"
            className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
          >
            {error}
          </p>
        ) : null}

        {!loading && proposal?.situation ? (
          <BaasProposalSituationCard situation={proposal.situation} />
        ) : null}

        {!loading && proposal ? (
          <div className="rounded-xl border border-gray-200 bg-white px-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
            <DetailRow
              label={intl.get("baas.accounts.proposalModal.proposalId")}
              value={proposal.proposalId != null ? String(proposal.proposalId) : null}
              mono
            />
            <DetailRow
              label={intl.get("baas.accounts.proposalModal.statusId")}
              value={
                proposal.situation?.statusId != null
                  ? String(proposal.situation.statusId)
                  : null
              }
            />
            <DetailRow
              label={intl.get("baas.accounts.proposalModal.status")}
              value={proposal.situation?.description || null}
            />
            <DetailRow
              label={intl.get("baas.accounts.proposalModal.nationalId")}
              value={proposal.nationalId || personal?.personalDocument}
              mono
            />
            <DetailRow
              label={intl.get("baas.accounts.proposalModal.personalName")}
              value={personal?.personalName}
            />
            <DetailRow
              label={intl.get("baas.accounts.proposalModal.personalEmail")}
              value={personal?.personalEmail}
            />
            <DetailRow
              label={intl.get("baas.accounts.proposalModal.phone")}
              value={formatPhone(personal)}
            />
            <DetailRow
              label={intl.get("baas.accounts.proposalModal.creditStatus")}
              value={credit?.status}
            />
            <DetailRow
              label={intl.get("baas.accounts.proposalModal.creditMessage")}
              value={credit?.message}
            />
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 px-5 py-3 dark:border-brand-dark-border">
        <button
          type="button"
          className="btn-outline w-auto !text-xs"
          onClick={() => {
            void loadProposal();
          }}
          disabled={loading || !entityId}
        >
          <TbRefresh className="h-4 w-4" />
          {intl.get("baas.accounts.proposalModal.refresh")}
        </button>
        <button
          type="button"
          className="btn-primary w-auto !text-xs"
          onClick={onClose}
        >
          {intl.get("baas.accounts.proposalModal.close")}
        </button>
      </div>
    </Dialog>
  );
};

export default BaasAccountProposalModal;
